import { Avatar, Box, Grid, IconButton, Tooltip } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CollapsibleFilterCard from "../../components/CollapsibleFilterCard/CollapsibleFilterCard";
import CustomCard from "../../components/CustomCard/CustomCard";
import CustomTabs from "../../components/CustomTabs/CustomTabs";
import Layout from "../../components/Layout/Layout";
import QueryPageTitleCard from "../../components/QueryPageTitleCard/QueryPageTitleCard";
import { routesConfig } from "../../config/routes";
import { getFormattedTimestamp } from "../../utils/dateFormatter";
import PipelineDeliveriesTab from "./Sections/PipelineDeliveriesTab";
import { StyledIcon } from "../../components/StyledIcon/StyledIcon";
import { getFirstDayOfCurrentMonth } from "../../utils";
import CustomButton, {
  CustomButtonVariant,
} from "../../components/CustomButton/CustomButton";
import DetailsModalTable from "./Sections/DetailsModalTable";
import CalendarRange from "../../components/CalendarRange/CalendarRange";
import { useAuth } from "../../contexts/authentication/AuthenticationContext";
import { TypesPermissions } from "../../components/Routes/TypesPermissions";
import { Roles } from "../../components/SideBar/Content/ListItem";
import { AuthenticationContextData } from "../../contexts/authentication/domain";
import MultipleSelectDistributors from "../../components/MultipleSelectDistributors/MultipleSelectDistributors";
import { useDistributor } from "../../contexts/distributor/DistributorContext";

import { sidebar } from "../../utils/featureFlags";
import { useFlags } from "flagsmith/react";
import {
  TableRow,
  openedInitQuery,
  clearedInitQuery,
  rejectedInitQuery,
} from "./models/default";

import SelectSubsidiaries from "../../components/SelectSubsidiary/SelectSubsidiary";
import {
  NO_OPEN_ORDERS_TITLE,
  NO_CLEARED_ORDERS_TITLE,
  NO_REJECTED_ORDERS_TITLE,
} from "../../utils/titles";

const PipelineDeliveriesPage: React.FC = () => {
  const flags = useFlags(sidebar);
  const currentDay = new Date();
  const { selectedDistributorsStoraged, subsidiaries } = useDistributor();
  const { authByPermission, permissionSet, roles } =
    useAuth() as AuthenticationContextData;
  const navigate = useNavigate();
  const [openedCount, setOpenedCount] = useState(0);
  const [clearedCount, setClearedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [dateFilterFrom, setDateFilterFrom] = useState<Date | null>(null);
  const [dateFilterTo, setDateFilterTo] = useState<Date | null>(null);
  const [clearValueCalendar, setClearValueCalendar] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<TableRow | null>(null);
  const [codeSubsidiary, setCodeSubsidiary] = useState<string | undefined>(
    undefined
  );

  const handleClose = () => {
    setIsOpen(false);
  };

  const clearFilters = () => {
    setDateFilterFrom(null);
    setDateFilterTo(null);
    setClearValueCalendar(true);
    setCodeSubsidiary(undefined);
  };

  const selectedDistributors = useMemo(() => {
    return selectedDistributorsStoraged.join(",");
  }, [selectedDistributorsStoraged]);

  const handleOpenDetails = (row: TableRow) => {
    setModalData(row);
    setIsOpen(true);
  };

  const isAllowed = useMemo(() => {
    return (
      authByPermission(
        permissionSet.SYSTEM_MODULES,
        roles.has(Roles.ADMIN),
        TypesPermissions.PIPELINES_REQUEST
      ) && flags.operational_new_pipelines.enabled
    );
  }, [authByPermission, flags]);

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{
              width: 60,
              height: 60,
            }}
          />
        ),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <QueryPageTitleCard
                title="Entregas dutoviárias"
                componentItem={
                  <CustomButton
                    title="Novo pedido de bombeio"
                    onClick={() => {
                      navigate(routesConfig.OPERATIONAL().PIPELINES_REQUEST);
                    }}
                    variant={
                      isAllowed
                        ? CustomButtonVariant.CONTAINED
                        : CustomButtonVariant.DISABLED
                    }
                    disabled={!isAllowed}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CollapsibleFilterCard
                title="Pedidos de bombeio"
                subtitle={getFormattedTimestamp(new Date())}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <MultipleSelectDistributors
                      maxWidth={1200}
                      label={"Selecione o cliente"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <SelectSubsidiaries
                      label="Filial"
                      valueSubsidiary={subsidiaries.find(
                        (item) => item.id === codeSubsidiary
                      )}
                      onChangeValue={(selectedOption) => {
                        if (selectedOption) {
                          setCodeSubsidiary(selectedOption.id);
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <CalendarRange
                      name="date"
                      label="Data de agendamento"
                      firstDayOfMonth={getFirstDayOfCurrentMonth()}
                      lastDayOfMonth={currentDay}
                      clearValueCalendar={clearValueCalendar}
                      onChangeDateStartEnd={(dateBegin, dateEnd) => {
                        setClearValueCalendar(false);
                        // eslint-disable-next-line
                        if (dateBegin && dateEnd) {
                          setDateFilterFrom(dateBegin);
                          setDateFilterTo(dateEnd);
                        }
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    justifyContent="flex-end"
                    gap={1}
                  >
                    <CustomButton
                      title="Limpar filtros"
                      variant={CustomButtonVariant.OUTLINED}
                      onClick={clearFilters}
                    />
                  </Grid>
                </Grid>
              </CollapsibleFilterCard>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomCard
            sx={{
              boxShadow: "none",
              border: "1px solid #DFE0EB",
              padding: 2.4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Grid container>
              <Grid item xs={12}>
                <DetailsModalTable
                  open={isOpen}
                  onClose={handleClose}
                  modalData={modalData}
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTabs
                  tabsHeader={[
                    `Em aberto (${openedCount})`,
                    `Faturado (${clearedCount})`,
                    `Reprovado (${rejectedCount})`,
                  ]}
                  body={[
                    <PipelineDeliveriesTab
                      key={0}
                      initQuery={openedInitQuery}
                      emptyTitle={NO_OPEN_ORDERS_TITLE}
                      emptySubtitle={`Para pedir clique no botão "Novo pedido de bombeio"`}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setOpenedCount(count);
                      }}
                      menuIcon={(item) => (
                        <Tooltip title="Ver detalhes">
                          <IconButton onClick={() => handleOpenDetails(item)}>
                            <StyledIcon iconType="info" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />,
                    <PipelineDeliveriesTab
                      key={1}
                      initQuery={clearedInitQuery}
                      emptyTitle={NO_CLEARED_ORDERS_TITLE}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setClearedCount(count);
                      }}
                      menuIcon={(item) => (
                        <Tooltip title="Ver detalhes">
                          <IconButton onClick={() => handleOpenDetails(item)}>
                            <StyledIcon iconType="info" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />,
                    <PipelineDeliveriesTab
                      key={2}
                      initQuery={rejectedInitQuery}
                      emptyTitle={NO_REJECTED_ORDERS_TITLE}
                      subsidiary={codeSubsidiary}
                      dateFilterFrom={
                        dateFilterFrom || getFirstDayOfCurrentMonth()
                      }
                      dateFilterTo={dateFilterTo || currentDay}
                      customerCompanyIds={selectedDistributors}
                      onChangeCount={(count) => {
                        setRejectedCount(count);
                      }}
                      menuIcon={(item) => (
                        <Tooltip title="Ver detalhes">
                          <IconButton onClick={() => handleOpenDetails(item)}>
                            <StyledIcon iconType="info" />
                          </IconButton>
                        </Tooltip>
                      )}
                    />,
                  ]}
                />
              </Grid>
            </Grid>
          </CustomCard>
        </Box>
      </Box>
    </Layout>
  );
};

export default PipelineDeliveriesPage;
